import {
  collection,
  doc,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { OrderedMap } from "immutable";
import PropTypes from "prop-types";
import { collectionData, docData } from "rxfire/firestore";
import { Subject } from "rxjs";
import { map, tap } from "rxjs/operators";

import { firestore } from "../firebase";
import { createdAt, updatedAt } from "../lib/timestamp";
import { collectionName as projectSlugCollectionName } from "./project-slugs";

export const subject = new Subject();

let data = OrderedMap();
let syncAt = 0;

export function clear() {
  data = OrderedMap();
  syncAt = 0;
}

export function getData() {
  return data.toArray().map(([, item]) => item);
}

export const collectionName = "projects";

export const propTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
  projectName: PropTypes.string.isRequired,
});

export const store = {
  /**
   * List projects
   * @param {string} maintainer - User ID to filter projects by ownership
   * @param {Object} options - Options
   * @param {boolean} options.allProjects - If true, list all projects (for superadmins)
   */
  list: (maintainer, { allProjects = false } = {}) => {
    // Build query constraints
    const constraints = [
      orderBy("updatedAt", "desc"),
      where("updatedAt", ">", Timestamp.fromMillis(syncAt)),
    ];

    // Only filter by owner if not listing all projects (superadmin mode)
    if (!allProjects) {
      constraints.push(where("owners", "array-contains", maintainer));
    }

    const projectsRef = query(
      collection(firestore, collectionName),
      ...constraints
    );

    return collectionData(projectsRef, { idField: "id" }).pipe(
      map((items) => {
        const newItems = OrderedMap(items.map((i) => [i.id, i]));
        // Merge new items into cache - newItems takes precedence over old data
        const newData = data.merge(newItems);
        data = newData;

        // Return all projects - filtering is done in the component
        return data.toArray().map(([, item]) => item);
      }),
      tap(() => (syncAt = new Date().getTime()))
    );
  },
  getById: (id) => {
    const docRef = doc(firestore, collectionName, id);

    return docData(docRef, { idField: "id" });
  },
  getBySlug: (slug, maintainer) => {
    const slugRef = query(
      collection(firestore, collectionName),
      orderBy("updatedAt", "asc"),
      where("slug", "==", slug),
      where("maintainers", "array-contains", maintainer)
    );

    return collectionData(slugRef, { idField: "id" }).pipe(
      map((items) => items[0])
    );
  },
  update: (id, data) => {
    const projectRef = doc(firestore, collectionName, id);

    return updateDoc(projectRef, { ...data, ...updatedAt() }).then(
      () => projectRef
    );
  },
  write: (newDoc) => {
    const batch = writeBatch(firestore);
    const projectRef = doc(collection(firestore, collectionName));
    const projectSlugRef = doc(
      firestore,
      projectSlugCollectionName,
      newDoc.slug
    );

    batch.set(projectSlugRef, { projectId: projectRef.id, ...createdAt() });
    batch.set(projectRef, { ...newDoc, ...createdAt() });

    return batch.commit().then(() => projectRef);
  },
};
